import { createSelector } from '@reduxjs/toolkit';

import { getName } from '@/shared/lib';

import { RootState } from '@/app/model/store';

import { getPatientYourPermissions } from '../lib';

import { patientAdapter } from './patientSlice';

export const patientSelectors = patientAdapter.getSelectors(
  (state: RootState) => state.patient,
);

export const { selectAll, selectById, selectTotal, selectEntities, selectIds } =
  patientSelectors;

export const selectPatientByID = (patientID: string) => (state: RootState) =>
  selectById(state, patientID);

export const selectCurrentPatient = (state: RootState) =>
  state.patient.currentPatient;

export const selectCurrentPatientID = (state: RootState) =>
  selectCurrentPatient(state)?.ID;

const selectSharedWithMeCount = (state: RootState) =>
  state.patient.sharedWithMeCount;
const sharedByMeCount = (state: RootState) => state.patient.sharedByMeCount;
const totalOrganizationPatientCount = (state: RootState) =>
  state.patient.totalOrganizationPatientCount;

export const selectCounters = createSelector(
  selectSharedWithMeCount,
  sharedByMeCount,
  totalOrganizationPatientCount,
  (sharedWithMeCount, sharedByMeCount, totalOrganizationPatientCount) => ({
    sharedWithMeCount,
    sharedByMeCount,
    totalOrganizationPatientCount,
  }),
);

// One memoized selector instance per patientID; recreated factories would
// return a fresh array on every call and trip the reselect reference warning
// (plus re-render every subscriber on each store update).
const treatingDoctorsIDsSelectors = new Map<
  string,
  (state: RootState) => string[]
>();

export const selectTreatingDoctorsIDs = (patientID: string) => {
  let selector = treatingDoctorsIDsSelectors.get(patientID);

  if (!selector) {
    selector = createSelector(
      (state: RootState) => patientSelectors.selectById(state, patientID),
      (patient) =>
        patient?.Doctors.filter(
          (doctor) =>
            doctor.Membership.case === 'TeamMember' && doctor.Membership.value,
        )
          .map((doctor) =>
            doctor.Membership.case === 'TeamMember'
              ? doctor.Membership.value.StaffID
              : undefined,
          )
          .filter((id): id is string => id !== undefined) ?? [],
    );

    treatingDoctorsIDsSelectors.set(patientID, selector);
  }

  return selector;
};

// TODO: [1/l] Replace instances where patient is retrieved from the store solely for the purpose of obtaining the name (getPatientFullName) with selectPatientFullName
export const selectPatientFullName = (patientID: string) =>
  createSelector(
    (state: RootState) => patientSelectors.selectById(state, patientID),
    (state: RootState) => state.user.locale,
    (patient, userLocale) =>
      getName(
        patient?.PersonalData?.FirstName,
        patient?.PersonalData?.LastName,
        { userLocale },
      ),
  );

export const selectLoading = (state: RootState) => state.patient.loading;

export const selectBlackCatPatientID =
  (patientID: string) => (state: RootState) =>
    selectById(state, patientID)?.BlackCatID;

export const selectPatientYourPermissions = (patientID: string) =>
  createSelector(selectPatientByID(patientID), (patient) =>
    getPatientYourPermissions(patient),
  );

export const selectClinicalCaseDescription = createSelector(
  selectCurrentPatient,
  (patient) => patient?.ClinicalCaseDescription,
);
